import { shallowEqual, useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { RoleType, useCurrentUser } from '@feature/security';

import { getTravelDayBalancePreviewOptions, getTravelPaperTitleCheckOptions } from './model/TravelRequestQuery';
import { TravelRequestSchema } from './model/TravelRequestSchema';
import {
  AbsenceMotivation,
  TravelActivityType,
  TravelDayBalancePreview,
  TravelDayBalancePreviewParams,
  TravelDayTotals,
  TravelPaperTitleCheckParams,
  TravelRequestAbsence,
  absenceMotivationTakesLeave,
} from './model/TravelRequestTypes';

type SetValue = UseFormSetValue<TravelRequestSchema>;
type GetValues = UseFormGetValues<TravelRequestSchema>;

export interface TravelDuration {
  calendar: number;
  working: number;
}

interface TravelDayBalanceSource {
  departureDate: string | null;
  returnDate: string | null;
  activityType: TravelActivityType;
  presentsPaper: boolean | null;
  absence: Pick<TravelRequestAbsence, 'absenceMotivation' | 'vacationDays' | 'unpaidDays'> | null;
}

interface TravelPaperTitleSource {
  activityType: TravelActivityType;
  presentsPaper: boolean | null;
  paperTitle: string | null;
}

const PREVIEW_DEBOUNCE_MS = 400;

function toTotals(preview: TravelDayBalancePreview): TravelDayTotals {
  const { workingDays, capUsableDays, allowedMotivations, enforcement } = preview;
  return { workingDays, capUsableDays, allowedMotivations, enforcement };
}

function sameTotals(kept: TravelDayTotals, preview: TravelDayBalancePreview) {
  return (
    kept.workingDays === preview.workingDays &&
    kept.capUsableDays === preview.capUsableDays &&
    kept.enforcement.dayCap === preview.enforcement.dayCap &&
    kept.enforcement.leaveDays === preview.enforcement.leaveDays &&
    kept.allowedMotivations.join() === preview.allowedMotivations.join()
  );
}

export function travelTripPeriod(departureDate: string | null, returnDate: string | null) {
  return !departureDate || !returnDate || returnDate < departureDate ? null : { departureDate, returnDate };
}

// The event period stays out: no count in the answer depends on it, so editing it must not refetch.
export function toTravelDayBalancePreviewParams(
  source: TravelDayBalanceSource,
  requestId: number | null = null,
): TravelDayBalancePreviewParams | null {
  const { activityType, presentsPaper, absence } = source;
  const period = travelTripPeriod(source.departureDate, source.returnDate);
  if (period == null) {
    return null;
  }

  return {
    ...period,
    activityType,
    presentsPaper,
    absenceMotivation: absence?.absenceMotivation ?? null,
    vacationDays: absence?.vacationDays ?? null,
    unpaidDays: absence?.unpaidDays ?? null,
    requestId,
  };
}

// Takes `watch` rather than useWatch, which subscribes after the effect applying an edited request's values and would miss them.
export function useTravelFormDayBalancePreview(
  watch: UseFormWatch<TravelRequestSchema>,
  requestId: number | null = null,
) {
  const [departureDate, returnDate, activityType, presentsPaper, absenceMotivation, vacationDays, unpaidDays] = watch([
    'departureDate',
    'returnDate',
    'activityType',
    'presentsPaper',
    'absence.absenceMotivation',
    'absence.vacationDays',
    'absence.unpaidDays',
  ]);
  const params = useMemo(
    () =>
      toTravelDayBalancePreviewParams(
        {
          departureDate,
          returnDate,
          activityType,
          presentsPaper,
          absence: { absenceMotivation, vacationDays, unpaidDays },
        },
        requestId,
      ),
    [departureDate, returnDate, activityType, presentsPaper, absenceMotivation, vacationDays, unpaidDays, requestId],
  );
  const [debounced] = useDebouncedValue(params, PREVIEW_DEBOUNCE_MS);

  // The server check at submit stands regardless, so a failed preview is not retried.
  const query = useQuery({ ...getTravelDayBalancePreviewOptions(debounced), retry: false });
  // Until the debounce settles, the query still answers for the previous inputs.
  const current = params != null && shallowEqual(params, debounced);
  const failed = current && query.isError;
  const preview = current && !failed ? query.data : undefined;

  // Leave days and the motivation leave these totals as they are, so they outlive the refetch each keystroke causes.
  const totalsKey =
    params == null ? null : [departureDate, returnDate, activityType, presentsPaper, requestId].join('|');
  const [kept, setKept] = useState<{ key: string; totals: TravelDayTotals } | null>(null);
  if (preview != null && totalsKey != null && (kept?.key !== totalsKey || !sameTotals(kept.totals, preview))) {
    setKept({ key: totalsKey, totals: toTotals(preview) });
  }
  const totals = preview ?? (kept != null && kept.key === totalsKey ? kept.totals : undefined);

  return { preview, totals, isLoading: params != null && preview == null && !failed, isError: failed };
}

export function toTravelPaperTitleCheckParams(
  source: TravelPaperTitleSource,
  requestId: number | null = null,
): TravelPaperTitleCheckParams | null {
  const paperTitle = source.paperTitle?.trim() ?? '';
  if (source.activityType !== 'SCIENTIFIC_EVENT' || source.presentsPaper !== true || paperTitle === '') {
    return null;
  }

  return { paperTitle, requestId };
}

export function isTravelPaperTitleTakenError(error: Error) {
  const code = isAxiosError(error) ? (error.response?.data?.type ?? error.response?.data?.message) : null;
  return code === 'TRAVEL_REQUEST_PAPER_TITLE_TAKEN';
}

// Advisory only: a pending or failed check answers undefined, since submit repeats the rule on the server.
export function useTravelPaperTitleCheck(params: TravelPaperTitleCheckParams | null) {
  // The endpoint refuses every role but the author.
  const enabled = useCurrentUser().currentRole === RoleType.AUTHOR && params != null;
  const query = useQuery({
    ...getTravelPaperTitleCheckOptions(params ?? { paperTitle: '', requestId: null }),
    enabled,
    retry: false,
  });

  return enabled && query.isSuccess ? query.data : undefined;
}

export function useTravelFormPaperTitleCheck(
  watch: UseFormWatch<TravelRequestSchema>,
  requestId: number | null = null,
) {
  const [activityType, presentsPaper, paperTitle] = watch(['activityType', 'presentsPaper', 'paperTitle']);
  const params = useMemo(
    () => toTravelPaperTitleCheckParams({ activityType, presentsPaper, paperTitle }, requestId),
    [activityType, presentsPaper, paperTitle, requestId],
  );
  const [debounced] = useDebouncedValue(params, PREVIEW_DEBOUNCE_MS);
  const check = useTravelPaperTitleCheck(debounced);

  // Until the debounce settles, the query still answers for the previous title.
  return params != null && shallowEqual(params, debounced) ? check : undefined;
}

// The day balance preview subtracts legal holidays too; this local count only stands in while it loads or fails.
export function travelDurationDays(startDate: string | null, endDate: string | null): TravelDuration | null {
  if (!startDate || !endDate) {
    return null;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return null;
  }

  const calendar = end.startOf('day').diff(start.startOf('day'), 'day') + 1;
  const working = Array.from({ length: calendar }, (_, offset) => start.add(offset, 'day').day()).filter(
    weekday => weekday !== 0 && weekday !== 6,
  ).length;

  return { calendar, working };
}

export function clearHiddenByActivityType(activityType: TravelActivityType, setValue: SetValue, getValues: GetValues) {
  const isErasmus = activityType === 'ERASMUS';

  if (!isErasmus && activityType !== 'PROFESSIONAL_DEVELOPMENT') {
    const expenses = getValues('expenses');
    const kept = expenses.filter(expense => expense.expenseType !== 'UMFST_MOBILITY_SUPPORT');
    if (kept.length !== expenses.length) {
      setValue('expenses', kept);
    }
  }

  if (isErasmus) {
    setValue('name', '');
    setValue('absence.absenceMotivation', null);
    setValue('absence.vacationDays', null);
    setValue('absence.unpaidDays', null);
    setValue('absence.ownProgramsMention', '');
    setValue('funding.perDiem', false);
    setValue('funding.fundingSourceNote', '');
    setValue('expenses', []);
  } else {
    setValue('erasmusType', null);
    setValue('funding.estimatedAmount', null);
    setValue('funding.estimatedAmountCurrency', null);
  }

  if (activityType !== 'SCIENTIFIC_EVENT') {
    setValue('presentsPaper', false);
    setValue('paperTitle', '');
  }
}

export function clearHiddenByPresentsPaper(presentsPaper: boolean | null, setValue: SetValue) {
  if (!presentsPaper) {
    setValue('paperTitle', '');
  }
}

export function clearHiddenByAbsenceMotivation(absenceMotivation: AbsenceMotivation | null, setValue: SetValue) {
  if (!absenceMotivationTakesLeave(absenceMotivation)) {
    setValue('absence.vacationDays', null);
    setValue('absence.unpaidDays', null);
  }

  if (absenceMotivation !== 'OWN_PROGRAMS') {
    setValue('absence.ownProgramsMention', '');
  }
}
